/**
 * Dashboard Metrics Hook
 *
 * Fetches and manages dashboard metrics with auto-refresh.
 * Phase 3.5-A2: Dashboard Data Hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDashboardMetrics } from '../api/dashboardApi';
import type { DashboardMetrics } from '../types';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export interface UseDashboardMetricsOptions {
  /** Auto-refresh interval in milliseconds. Set to 0 to disable. Default: 30000 */
  refreshInterval?: number;
  /** Whether to fetch immediately on mount. Default: true */
  fetchOnMount?: boolean;
}

export interface UseDashboardMetricsResult {
  data: DashboardMetrics | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useDashboardMetrics = (
  authFetch: AuthFetch,
  options: UseDashboardMetricsOptions = {}
): UseDashboardMetricsResult => {
  const { refreshInterval = 30000, fetchOnMount = true } = options;

  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(fetchOnMount !== false);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const isMounted = useRef(true);
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;
  const dataRef = useRef<DashboardMetrics | null>(null);

  const fetchData = useCallback(async () => {
    // Only show loading skeleton on initial fetch (no existing data)
    if (!dataRef.current) {
      setIsLoading(true);
    }
    setError(null);

    // Retry up to 3 times on initial fetch (handles cold-start API timing).
    // In tests, retries are disabled to keep behavior synchronous.
    const isTest = typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test';
    const maxRetries = isTest || dataRef.current ? 1 : 3;
    const retryDelayMs = 2000;
    let lastError: unknown = null;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const metrics = await fetchDashboardMetrics(authFetchRef.current);
        if (isMounted.current) {
          setData(metrics);
          dataRef.current = metrics;
          setLastUpdated(new Date());
        }
        lastError = null;
        break;
      } catch (err) {
        lastError = err;
        if (attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, retryDelayMs * (attempt + 1)));
        }
      }
    }

    if (lastError && isMounted.current) {
      setError(lastError instanceof Error ? lastError : new Error('Failed to fetch metrics'));
    }
    if (isMounted.current) {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (fetchOnMount) {
      fetchData();
    }
  }, [fetchOnMount, fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const intervalId = setInterval(fetchData, refreshInterval);
    return () => clearInterval(intervalId);
  }, [refreshInterval, fetchData]);

  // Cleanup
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return { data, isLoading, error, refresh: fetchData, lastUpdated };
};

export default useDashboardMetrics;
